import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/dialog'
import { Badge } from '@shared/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUser, 
  faCog, 
  faShieldAlt, 
  faClipboardList,
  faEye
} from '@fortawesome/free-solid-svg-icons'

interface AuditLogEntry {
  id: string
  timestamp: string
  user: string
  action: string
  resource: string
  details: string
  ip_address: string
  user_agent: string
  severity: 'low' | 'medium' | 'high' | 'critical'
}

interface AuditLogViewerDialogProps {
  isOpen: boolean
  onClose: () => void
  logEntry: AuditLogEntry | null
}

const AuditLogViewerDialog: React.FC<AuditLogViewerDialogProps> = ({ isOpen, onClose, logEntry }) => {
  if (!logEntry) return null

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500'
      case 'high': return 'bg-orange-500'
      case 'medium': return 'bg-yellow-500'
      case 'low': return 'bg-green-500'
      default: return 'bg-gray-500'
    }
  }

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return faUser
      case 'update': return faCog
      case 'delete': return faShieldAlt
      case 'login': return faUser
      case 'backup': return faClipboardList
      case 'maintenance': return faCog
      default: return faClipboardList
    }
  }

  const pretty = (val: any) => {
    try { 
      return JSON.stringify(val ?? null, null, 2) 
    } catch { 
      return String(val) 
    }
  }

  type Change = { path: string; before: any; after: any }
  const computeDiff = (before: any, after: any): Change[] => {
    const changes: Change[] = []
    const walk = (b: any, a: any, path: string[]) => {
      const bObj = b !== null && typeof b === 'object'
      const aObj = a !== null && typeof a === 'object'
      if (!bObj || !aObj) {
        if (JSON.stringify(b) !== JSON.stringify(a)) changes.push({ path: path.join('.'), before: b, after: a })
        return
      }
      const keys = Array.from(new Set([...(Object.keys(b || {})), ...(Object.keys(a || {}))]))
      for (const k of keys) {
        walk(b?.[k], a?.[k], [...path, k])
      }
    }
    walk(before ?? null, after ?? null, [])
    return changes
  }

  const oneLine = (val: any) => {
    try {
      const s = JSON.stringify(val ?? null)
      return typeof s === 'string' ? s : String(val ?? '')
    } catch {
      return String(val ?? '')
    }
  }

  const highlightDiff = (aRaw: string | undefined, bRaw: string | undefined): { beforeEl: JSX.Element; afterEl: JSX.Element } => {
    const a = typeof aRaw === 'string' ? aRaw : ''
    const b = typeof bRaw === 'string' ? bRaw : ''
    if (a === b) return { beforeEl: <span>{a}</span>, afterEl: <span>{b}</span> }
    const aLen = a.length, bLen = b.length
    let prefix = 0
    while (prefix < aLen && prefix < bLen && a[prefix] === b[prefix]) prefix++
    let suffix = 0
    while (
      suffix < aLen - prefix &&
      suffix < bLen - prefix &&
      a[aLen - 1 - suffix] === b[bLen - 1 - suffix]
    ) suffix++
    const aMid = a.slice(prefix, aLen - suffix)
    const bMid = b.slice(prefix, bLen - suffix)
    const aNode = (
      <span>
        <span>{a.slice(prefix ? 0 : 0, prefix)}</span>
        {aMid && <span className="bg-red-500/10 text-red-600 px-0.5 rounded">{aMid}</span>}
        <span>{a.slice(aLen - suffix)}</span>
      </span>
    )
    const bNode = (
      <span>
        <span>{b.slice(prefix ? 0 : 0, prefix)}</span>
        {bMid && <span className="bg-green-500/10 text-green-700 px-0.5 rounded">{bMid}</span>}
        <span>{b.slice(bLen - suffix)}</span>
      </span>
    )
    return { beforeEl: aNode as JSX.Element, afterEl: bNode as JSX.Element }
  }

  // Parse the details to extract before/after data
  const detailsObj = (() => {
    try {
      return JSON.parse(logEntry.details)
    } catch {
      return null
    }
  })()

  const before = detailsObj?.before || detailsObj?.old || null
  const after = detailsObj?.after || detailsObj?.new || detailsObj || null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-5xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
            Audit Log Entry Details
          </DialogTitle>
        </DialogHeader>
        
        {logEntry && (
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-muted-foreground">Time</div>
                <div className="font-mono">{new Date(logEntry.timestamp).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Actor</div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={getActionIcon(logEntry.action)} className="h-4 w-4 text-muted-foreground" />
                  {logEntry.user}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Action</div>
                <div><Badge variant="outline">{logEntry.action}</Badge></div>
              </div>
              <div>
                <div className="text-muted-foreground">Resource</div>
                <div>{logEntry.resource}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Severity</div>
                <div><Badge className={getSeverityColor(logEntry.severity)}>{logEntry.severity}</Badge></div>
              </div>
              <div>
                <div className="text-muted-foreground">IP / UA</div>
                <div className="font-mono truncate" title={logEntry.user_agent || ''}>{logEntry.ip_address || ''}</div>
              </div>
            </div>

            {/* Before/After Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="mb-1 font-medium">Before</div>
                <pre className="bg-muted p-3 rounded max-h-64 overflow-auto text-xs max-w-full whitespace-pre break-all">
                  <code>{pretty(before)}</code>
                </pre>
              </div>
              <div>
                <div className="mb-1 font-medium">After</div>
                <pre className="bg-muted p-3 rounded max-h-64 overflow-auto text-xs max-w-full whitespace-pre break-all">
                  <code>{pretty(after)}</code>
                </pre>
              </div>
            </div>

            {/* Changes List */}
            <div>
              <div className="mb-1 font-medium">Changes</div>
              {(() => {
                const diff = computeDiff(before, after)
                if (!diff.length) return <div className="text-sm text-muted-foreground">No field-level changes</div>
                return (
                  <div className="max-h-[40vh] overflow-auto">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Field</TableHead>
                            <TableHead>Before</TableHead>
                            <TableHead>After</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {diff.map((d, i) => {
                            const a = oneLine(d.before)
                            const b = oneLine(d.after)
                            const { beforeEl, afterEl } = highlightDiff(a, b)
                            return (
                              <TableRow key={i}>
                                <TableCell className="font-mono text-xs break-all">{d.path || '(root)'}</TableCell>
                                <TableCell className="font-mono text-xs break-all">{beforeEl}</TableCell>
                                <TableCell className="font-mono text-xs break-all">{afterEl}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )
              })()}
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AuditLogViewerDialog